import React, { useState, useMemo } from 'react';
import { Container, Card, CardBody, Row, Col } from 'reactstrap';
import {
  Tiles,
  Table,
} from 'components/templates/rpm/practice/patientsStatusReport';
import AutoCompleteClinics from 'components/molecules/autocompleteFilter/clinic';
import DownloadRoundedIcon from 'components/atoms/icons/downloadRoundedIcon';
import Pagination from 'components/molecules/paginationTransition';
import { showResult } from 'utils';
import useTable from 'utils/useTable';
import common from 'constants/common';
import {
  API_PATIENTS_STATUS_REPORT,
  API_PATIENTS_STATUS_COUNT,
  PATIENTS_STATUS_CSV_REPORT,
} from 'services/dataService/resources';
import dataService from 'services/dataService';
import withLoader from 'components/organisms/withLoader';
import forms from 'constants/forms';
import { skipEmptyStrings } from 'utils/queryHelpers';

const DEFAULT_CLINIC_FILTER = forms.clinicFilterOptions[0];
const PER_PAGE = 100;

const PatientsStatusReport = () => {
  const [csvLoading, setCsvLoading] = useState(false);
  const {
    data: statuses,
    loading: statusesLoading,
    setFilters: setClinic,
  } = useTable(API_PATIENTS_STATUS_COUNT, null, {});

  const {
    data: patients,
    loading,
    page,
    setPage,
    filters,
    setFilters,
  } = useTable(API_PATIENTS_STATUS_REPORT, PER_PAGE, {
    statusUpdatedDateSort: 'asc',
  });

  const patientStatuses = useMemo(() => {
    if (!statuses) return;
    const orderedStatuses = {
      patient_referral: [statuses.patient_referral],
      referral_withdrawn: [statuses.referral_withdrawn],
      calling_queue: [statuses.calling_queue],
      onboarding: [statuses.onboarding],
      onboarding_unsuccessful: [statuses.onboarding_unsuccessful],
      patient_refused: [statuses.patient_refused],
      insurance_refused: [statuses.insurance_refused],
      patient_no_contact: [statuses.patient_no_contact],
      active: [statuses.active],
      pending_review: [statuses.pending_review],
      inactive_patient: [statuses.inactive_patient],
    };
    return orderedStatuses;
  }, [statuses]);

  const sendReport = async () => {
    setCsvLoading(true);
    const { error } = await dataService.createOne(
      PATIENTS_STATUS_CSV_REPORT,
      skipEmptyStrings(filters),
    );
    setCsvLoading(false);
    return showResult(error, common.submitSuccessTypes.sendReport);
  };

  return (
    <Container className="patients-status-report">
      {withLoader(
        statusesLoading,
        <Tiles
          tiles={patientStatuses}
          filters={filters}
          setFilter={(statusFilter) =>
            setFilters({ ...filters, ...statusFilter })
          }
        />,
      )}
      <Card className="report card-border card--full-width">
        <CardBody className="card-outer">
          <div className="performance-review__header">
            <h3 className="report__header-text" data-cy="status-report__title">
              Report: Patients Status
            </h3>
            <div className="performance-review__header-icon">
              {withLoader(
                csvLoading,
                <button onClick={sendReport}>
                  <DownloadRoundedIcon className="performance-review__header-icon-svg" />
                  <br />
                  <span>Download</span>
                </button>,
              )}
            </div>
          </div>
          <Row>
            <Col xl="2" xs="12" md="6" className="report__filter">
              <p
                className="report__filter-title"
                data-cy="status-report__clinic-filter-label"
              >
                Clinic
              </p>
              <AutoCompleteClinics
                filter={DEFAULT_CLINIC_FILTER}
                handleClick={({ value }) => {
                  setClinic({ clinicGuid: value });
                  setFilters({ ...filters, clinicGuid: value });
                }}
              />
            </Col>
          </Row>
          {withLoader(
            loading,
            <>
              <Table
                data={patients?.items}
                setFilters={(sorts) => setFilters({ ...filters, ...sorts })}
                filters={filters}
              />
              <div className="report__pagination-button flex-row-reverse">
                <Pagination
                  title=""
                  page={page}
                  pageTotal={patients?.items?.length}
                  itemsPerPage={PER_PAGE}
                  total={patients?.count}
                  handleFirstClick={() => setPage(1)}
                  handleLastClick={() =>
                    setPage(Math.ceil(patients?.count / PER_PAGE))
                  }
                  handlePrevClick={() => setPage(page - 1)}
                  handleNextClick={() => setPage(page + 1)}
                  handleGoToPage={(numberPage) => setPage(numberPage)}
                />
              </div>
            </>,
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default PatientsStatusReport;
